import { useState } from "react";
import { BRAND, productTypeBg } from "../types";
import type { InquiryOrder } from "../types";

export default function QuoteConfirmPage({
  order,
  onBack,
  onConfirm,
}: {
  order: InquiryOrder;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const [agreed, setAgreed] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: "#F4F5F9" }}>
      {/* Header */}
      <div
        className="sticky top-0 z-40 relative overflow-hidden"
        style={{ background: "linear-gradient(145deg,#1A2238 0%,#2D3561 70%,#3D5A80 100%)" }}
      >
        <div className="relative px-4 pb-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0"
              style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)" }}
            >
              <i className="ri-arrow-left-line" style={{ fontSize: 18, color: "#fff" }} />
            </button>
            <div>
              <h1 className="text-base font-bold text-white">查看方案并确认</h1>
              <p className="text-xs" style={{ color: "rgba(255,255,255,0.45)" }}>请仔细核对产品信息和价格</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-3 pb-36">
        {/* 顾问推荐语 */}
        {order.staffNote && (
          <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
            <div className="flex items-center gap-2 mb-2">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #FF6B35, #FF9A5C)" }}
              >
                {order.assignedTo?.slice(-1) ?? "顾"}
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-800">{order.assignedTo ?? "专属顾问"}</div>
                <div className="text-xs text-gray-400">顾问推荐语</div>
              </div>
            </div>
            <div className="px-3 py-2.5 rounded-xl" style={{ background: "#FFF8F0", border: "1px solid #FFE4CC" }}>
              <p className="text-sm text-gray-700 leading-relaxed">{order.staffNote}</p>
            </div>
          </div>
        )}

        {/* 行程概览 */}
        <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-map-2-line" style={{ color: BRAND, fontSize: 14 }} />
            <span className="text-sm font-bold text-gray-800">行程概览</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { icon: "ri-map-pin-line", label: "目的地", value: order.needs.destination },
              { icon: "ri-calendar-line", label: "出行时间", value: order.needs.dates.split("（")[0] },
              { icon: "ri-group-line", label: "出行人数", value: `${order.needs.travelers}人` },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center p-2.5 rounded-xl" style={{ background: "#F8F9FC" }}>
                <i className={item.icon} style={{ color: BRAND, fontSize: 16, marginBottom: 4 }} />
                <div className="text-xs text-gray-400 mb-0.5">{item.label}</div>
                <div className="text-xs font-bold text-gray-800 text-center">{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 产品明细 */}
        <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-list-check-2" style={{ color: BRAND, fontSize: 14 }} />
            <span className="text-sm font-bold text-gray-800">产品明细</span>
          </div>
          <div className="space-y-3">
            {order.products.map((p, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded-xl"
                style={{ background: "#F8F9FC", border: "1px solid #F0F0F0" }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0">
                  {p.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium border ${productTypeBg[p.type]}`}>
                      {p.label}
                    </span>
                    {p.originalPrice && p.quotedPrice && p.quotedPrice < p.originalPrice && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-red-50 text-red-600 border border-red-100">
                        优惠
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-semibold text-gray-800 truncate">{p.name}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{p.spec}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-bold" style={{ color: BRAND }}>
                    ¥{(p.quotedPrice ?? p.originalPrice ?? 0).toLocaleString()}
                  </div>
                  {p.originalPrice && p.quotedPrice && p.originalPrice !== p.quotedPrice && (
                    <div className="text-xs text-gray-400 line-through">¥{p.originalPrice.toLocaleString()}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 费用汇总 */}
        {order.totalQuotedPrice !== undefined && (
          <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-money-cny-circle-line" style={{ color: BRAND, fontSize: 14 }} />
              <span className="text-sm font-bold text-gray-800">费用汇总</span>
            </div>
            <div className="space-y-2">
              {order.products.map((p, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">{p.label}：{p.name}</span>
                  <span className="text-xs font-semibold text-gray-700">
                    ¥{(p.quotedPrice ?? p.originalPrice ?? 0).toLocaleString()}
                  </span>
                </div>
              ))}
              <div className="h-px bg-gray-100 my-2" />
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-gray-800">套餐总价</span>
                <span className="text-xl font-bold" style={{ color: BRAND }}>
                  ¥{order.totalQuotedPrice.toLocaleString()}
                </span>
              </div>
              {order.depositAmount && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">首付定金（30%）</span>
                    <span className="text-sm font-semibold" style={{ color: "#059669" }}>
                      ¥{order.depositAmount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">出发前尾款</span>
                    <span className="text-sm font-semibold text-gray-600">
                      ¥{((order.totalQuotedPrice ?? 0) - (order.depositAmount ?? 0)).toLocaleString()}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* 服务保障 */}
        <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-shield-check-line" style={{ color: "#059669", fontSize: 14 }} />
            <span className="text-sm font-bold text-gray-800">服务保障</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { icon: "ri-shield-check-line", text: "正规持牌服务商", color: "#059669" },
              { icon: "ri-refund-2-line", text: "7天无理由退款", color: "#0891B2" },
              { icon: "ri-customer-service-2-line", text: "24小时顾问服务", color: BRAND },
              { icon: "ri-file-text-line", text: "电子合同保障", color: "#7C3AED" },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: "#F8F9FC" }}>
                <i className={item.icon} style={{ color: item.color, fontSize: 14 }} />
                <span className="text-xs text-gray-600">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 协议 */}
        <div className="px-1">
          <button
            onClick={() => setAgreed(!agreed)}
            className="flex items-start gap-2 cursor-pointer"
          >
            <div
              className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 mt-0.5 transition-all"
              style={{
                background: agreed ? BRAND : "transparent",
                border: `1.5px solid ${agreed ? BRAND : "#D1D5DB"}`,
              }}
            >
              {agreed && <i className="ri-check-line text-white" style={{ fontSize: 10 }} />}
            </div>
            <span className="text-xs text-gray-500 leading-relaxed">
              我已阅读并同意
              <span style={{ color: BRAND }}>《旅游服务协议》</span>
              和
              <span style={{ color: BRAND }}>《退改规则》</span>
              ，确认以上产品信息和价格无误
            </span>
          </button>
        </div>
      </div>

      {/* 底部操作 */}
      <div
        className="fixed bottom-0 left-0 right-0 px-4"
        style={{ background: "rgba(255,255,255,0.97)", borderTop: "1px solid #F0F0F0", backdropFilter: "blur(20px)", paddingTop: "8px", paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="flex gap-3">
          <button
            onClick={onBack}
            className="flex-1 py-3.5 rounded-2xl text-sm font-semibold cursor-pointer whitespace-nowrap border border-gray-200 text-gray-600"
          >
            再想想
          </button>
          <button
            onClick={onConfirm}
            disabled={!agreed}
            className="flex-2 py-3.5 rounded-2xl text-sm font-bold text-white cursor-pointer whitespace-nowrap active:scale-[0.98] transition-all"
            style={{
              flex: 2,
              background: agreed ? `linear-gradient(135deg, ${BRAND}, #FF9A5C)` : "#E5E7EB",
              color: agreed ? "#fff" : "#9CA3AF",
            }}
          >
            确认方案，去支付
          </button>
        </div>
      </div>
    </div>
  );
}
